import { Box, Chip, Link as MuiLink, Typography, alpha } from '@mui/material';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { getFormattedRuPhone } from 'core/utils/commonUtils';
import { getOrderActionName } from 'core/utils/orderUtils';
import { isEmpty, isNil } from 'lodash';
import { Link as RouterLink } from 'react-router-dom';

import { getActionColor, getOrderPath } from '../utils';
import { MonthDaysList } from './MonthDayList/MonthDaysList';
import type { ViewComponentProps } from './types';
import type { GridColDef } from '@mui/x-data-grid';
import type { OrderActionsLog } from 'models/orderActionsLog';
import type { FC } from 'react';

const MonthlyListViewTable: FC<ViewComponentProps> = (props) => {
  const { groupedByMonthAndDay, selectedDay, handleDayClick } = props;

  const columns: Array<GridColDef<OrderActionsLog>> = [
    {
      field: 'action',
      headerName: 'Этап',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const actionColor = getActionColor(params.value);
        return (
          <Chip
            label={getOrderActionName(params.value)}
            size='small'
            sx={{
              backgroundColor: alpha(actionColor, 0.1),
              color: actionColor,
              fontWeight: 'medium',
            }}
          />
        );
      },
    },
    {
      field: 'orderNumber',
      headerName: 'Заказ',
      flex: 1,
      minWidth: 100,
      valueGetter: (_value, row) => row.order.number,
      renderCell: (params) => (
        <MuiLink
          component={RouterLink}
          to={getOrderPath(params.row.order.state, params.row.order.id)}>
          {params.value}
        </MuiLink>
      ),
    },
    {
      field: 'orderAddress',
      headerName: 'Адрес',
      flex: 1.5,
      minWidth: 150,
      valueGetter: (_value, row) => row.order.address,
      renderCell: (params) => (
        <Typography fontSize={'smaller'}>{params.value}</Typography>
      ),
    },
    {
      field: 'orderClientFullName',
      headerName: 'Клиент',
      flex: 1,
      minWidth: 100,
      valueGetter: (_value, row) => row.order.client.fullName,
      renderCell: (params) => (
        <MuiLink
          fontSize={'small'}
          component={RouterLink}
          to={`/clients/${params.row.order.client.id}`}>
          {params.value}
        </MuiLink>
      ),
    },
    {
      field: 'orderClientPhone',
      headerName: 'Телефон клиента',
      flex: 1,
      minWidth: 140,
      valueGetter: (_value, row) => row.order.client.phone,
      valueFormatter: (value: string) => getFormattedRuPhone(value),
      renderCell: (params) => {
        const value: string = params.value;
        return !isEmpty(value) ? (
          <MuiLink fontSize={'small'} href={`tel:+${value}`}>
            {getFormattedRuPhone(value)}
          </MuiLink>
        ) : (
          ''
        );
      },
    },
    {
      field: 'user',
      headerName: 'Ответственный',
      flex: 1,
      minWidth: 100,
      valueGetter: (_value, row) => row.user?.fullName ?? 'Не задан',
      renderCell: (params) => {
        if (isNil(params.row.user)) return params.value;
        return (
          <MuiLink
            fontSize={'small'}
            component={RouterLink}
            to={`/users/${params.row.user?.id}`}>
            {params.value}
          </MuiLink>
        );
      },
    },
  ];

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      <Box
        sx={{ width: 'fit-content', minWidth: '145px', overflow: 'auto' }}
        borderRight={1}
        borderColor='divider'>
        <MonthDaysList
          groupedByMonthAndDay={groupedByMonthAndDay}
          selectedDay={selectedDay}
          handleDayClick={handleDayClick}
        />
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {selectedDay ? (
          <DataGrid
            columns={columns}
            rows={selectedDay.events}
            autoHeight
            hideFooterPagination
            hideFooter
            disableRowSelectionOnClick
            sx={{
              [`& .${gridClasses.cell}:not(.${gridClasses.cell}Checkbox):not([data-field="actions"])`]:
                { py: 1.5 },
              [`& .${gridClasses.cell}:focus`]: { outline: 'none' },
              [`& .${gridClasses.row}`]: { cursor: 'pointer' },
            }}
            getRowHeight={() => 'auto'}
          />
        ) : (
          <Typography
            variant='body1'
            color='text.secondary'
            sx={{ textAlign: 'center', mt: 4 }}>
            Выберите день для просмотра событий
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default MonthlyListViewTable;
